<template>
  <div :class="{ 'text-error': !!contractError }">{{ $t("tickets.field.contract") + (contractRequired ? " *" : "") }}</div>
  <v-autocomplete
    v-model="modelValue"
    :clearable="!readonly && !contractRequired"
    return-object
    :loading="loading || contractStatus === 'pending'"
    :density="density ?? 'default'"
    :items="contractOptions"
    :placeholder="$t('tickets.placeholder.contract')"
    :menu-icon="!!readonly ? '' : 'arrow_drop_down'"
    :readonly="readonly"
    :rules="rules"
    :disabled="disabled"
    item-title="description"
    :error-messages="
      errorMessages?.length ? errorMessages : error ? [$t('validation.required')] : contractError ? [contractError] : undefined
    "
    :error="error || !!contractError"
    :no-data-text="$t('noSelectableContracts')"
  >
    <template #selection>
      <div v-if="modelValue" class="text-body-medium text-truncate">
        <contract-status-icon
          v-if="selectedContractStatus && selectedContractStatus !== ContractStatus.APPROVED"
          class="mr-2"
          :status="selectedContractStatus"
        />
        {{ modelValue.description }}
      </div>
    </template>

    <template #item="{ item, props: itemProps }">
      <v-list-subheader
        v-if="allowedContracts.filter((contract) => contract.assets?.length).findIndex((contract) => contract._id === item._id) === 0"
      >
        {{ $t("asset") }}
      </v-list-subheader>
      <v-list-subheader
        v-else-if="
          showGenericContractTypeHeader &&
          allowedContracts.filter((contract) => !contract.assets?.length).findIndex((contract) => contract._id === item._id) === 0
        "
      >
        {{ $t("generic") }}
      </v-list-subheader>
      <v-list-item v-bind="itemProps">
        <contract-status-badge v-if="showStatus" :status="item.status" />
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import pick from "lodash-es/pick"

const modelValue = defineModel<{ _id: string; description: string } | null>()
const props = defineProps<{
  ticket: Partial<Pick<Ticket, "startAt" | "date" | "supplier" | "organization" | "asset" | "type">>
  /** disables the input */
  readonly?: boolean
  /** disables the input */
  disabled?: boolean
  loading?: boolean
  density?: "compact" | "default"
  label?: string
  rules?: Array<(value: unknown) => boolean | string>
  contractRequired?: boolean
  error?: boolean
  errorMessages?: Array<string>
  assetIdsOverride?: Array<string>
}>()
const $i18n = useI18n()
const { ticket, readonly, rules, contractRequired, error, errorMessages, assetIdsOverride } = toRefs(props)

const { getStoredContracts, getContractWithRecalculatedTicketStatuses } = useContractStore()

const { data: allContracts, status: contractStatus } = useLazyAsyncData(`stored-contracts`, () => getStoredContracts(), {
  default: () => [] as Array<Contract>,
})

const allowedContractStatuses = [ContractStatus.APPROVED, ContractStatus.PARTIALLY_APPROVED, ContractStatus.AWAITING_APPROVAL]

const ticketContracts = computed(() => getContractWithRecalculatedTicketStatuses(allContracts.value, ticket.value, assetIdsOverride.value))
const allowedContracts = computed(() =>
  ticketContracts.value
    .filter((contract) => allowedContractStatuses.includes(contract.status))
    .sort((contract1, contract2) => (contract2.assets.length ? 1 : 0) - (contract1.assets.length ? 1 : 0)),
)

const selectedContractStatus = computed(() => {
  if (!modelValue.value) {
    return undefined
  }

  const selectedContract = ticketContracts.value.find((contract) => contract._id === modelValue.value?._id)

  return selectedContract?.status
})

const contractOptions = computed(() => allowedContracts.value.map((contract) => pick(contract, ["_id", "description", "status"])))
const showStatus = computed(() => contractOptions.value.some((contract) => contract.status !== ContractStatus.APPROVED))

// Only show the generic sub header when there is more then 1 type of contract
const showGenericContractTypeHeader = computed(() => allowedContracts.value.filter((contract) => contract.assets?.length).length > 0)

const contractError = computed<false | string>(() => {
  if (!modelValue.value || readonly.value) return false

  if (!modelValue.value && contractRequired.value) {
    return $i18n.t("validation.required")
  }

  if (!contractOptions.value.some((contract) => contract._id === modelValue.value?._id)) {
    return $i18n.t("tickets.errors.contract.invalid")
  }

  return false
})
</script>

<!-- Only works on input with scoped styles -->
<style scoped>
@media (min-width: 960px) {
  .small-field-text :deep(.v-field__input),
  .small-field-text :deep(.v-field__input input),
  .small-field-text :deep(.v-field__input input::placeholder) {
    font-size: 0.875rem !important;
  }
}
</style>
